import {
  date,
  index,
  jsonb,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
} from "drizzle-orm/pg-core";
import { nanoid } from "nanoid";
import { user } from "./auth";
import { classTemplates } from "./classes";
import { formResponses, forms } from "./forms";
import { students } from "./students";

/** ฟอร์มโปรไฟล์ที่ seed ใน Form Builder — โค้ดผูกด้วย id ไม่ผูกด้วย label */
export const CLASS_ENROLLMENT_FORM_ID = "fl_frm_class_enrollment";

export const classEnrollmentFieldIds = {
  name: "fl_fld_enroll_name",
  nickname: "fl_fld_enroll_nickname",
  dateOfBirth: "fl_fld_enroll_dob",
  gender: "fl_fld_enroll_gender",
  phone: "fl_fld_enroll_phone",
  note: "fl_fld_enroll_note",
} as const;

export const classEnrollmentStatuses = [
  "pending",
  "contacted",
  "confirmed",
  "rejected",
  "duplicate",
  "converted",
] as const;
export type ClassEnrollmentStatus = (typeof classEnrollmentStatuses)[number];

export const classEnrollmentGenders = ["male", "female", "other"] as const;
export type ClassEnrollmentGender = (typeof classEnrollmentGenders)[number];

export type ClassEnrollmentMetadata = {
  source?: string;
  locale?: string;
  userAgent?: string;
  ip?: string;
};

/**
 * ใบสมัครเรียนออนไลน์ — ยืนยันที่เคาน์เตอร์ก่อนแปลงเป็นนักเรียน
 */
export const classEnrollmentApplications = pgTable(
  "class_enrollment_applications",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    formId: text("form_id")
      .notNull()
      .references(() => forms.id, { onDelete: "restrict" }),
    formResponseId: text("form_response_id")
      .notNull()
      .references(() => formResponses.id, { onDelete: "restrict" }),
    fullName: text("full_name").notNull(),
    nickname: text("nickname"),
    dateOfBirth: date("date_of_birth", { mode: "string" }).notNull(),
    gender: text("gender", { enum: classEnrollmentGenders }).notNull(),
    phoneNumber: text("phone_number").notNull(),
    status: text("status", { enum: classEnrollmentStatuses })
      .notNull()
      .default("pending"),
    adminNotes: text("admin_notes"),
    contactedAt: timestamp("contacted_at", {
      mode: "string",
      withTimezone: false,
    }),
    contactedBy: text("contacted_by").references(() => user.id, {
      onDelete: "set null",
    }),
    studentId: text("student_id").references(() => students.id, {
      onDelete: "set null",
    }),
    convertedAt: timestamp("converted_at", {
      mode: "string",
      withTimezone: false,
    }),
    convertedBy: text("converted_by").references(() => user.id, {
      onDelete: "set null",
    }),
    submittedAt: timestamp("submitted_at", {
      mode: "string",
      withTimezone: false,
    })
      .notNull()
      .defaultNow(),
    metadata: jsonb("metadata").$type<ClassEnrollmentMetadata>(),
    createdAt: timestamp("created_at", { mode: "string", withTimezone: false })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { mode: "string", withTimezone: false })
      .notNull()
      .defaultNow(),
  },
  (t) => [
    uniqueIndex("class_enrollment_applications_response_unique").on(
      t.formResponseId,
    ),
    index("class_enrollment_applications_by_status").on(t.status),
    index("class_enrollment_applications_by_phone").on(t.phoneNumber),
    index("class_enrollment_applications_by_form").on(t.formId),
    index("class_enrollment_applications_by_submitted_at").on(t.submittedAt),
  ],
);

/** คลาสที่ผู้สมัครเลือก — อย่างน้อย 1 แถวต่อใบสมัคร ตรวจที่ชั้นบริการ */
export const classEnrollmentApplicationClasses = pgTable(
  "class_enrollment_application_classes",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    applicationId: text("application_id")
      .notNull()
      .references(() => classEnrollmentApplications.id, { onDelete: "cascade" }),
    templateId: text("template_id")
      .notNull()
      .references(() => classTemplates.id, { onDelete: "restrict" }),
    createdAt: timestamp("created_at", { mode: "string", withTimezone: false })
      .notNull()
      .defaultNow(),
  },
  (t) => [
    uniqueIndex("class_enrollment_application_classes_unique").on(
      t.applicationId,
      t.templateId,
    ),
    index("class_enrollment_application_classes_by_template").on(t.templateId),
  ],
);
